import React, { useState, useCallback } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Upload } from "lucide-react";
import { cn } from "@/shared/utils/core";

interface DocumentUploadInputProps {
  accept?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  primaryMessage?: string;
  secondaryMessage?: string;
  disabled?: boolean;
  disabledMessage?: string;
  className?: string;
}

export default function DocumentUploadInput({
  accept = ".pdf,.jpg,.jpeg,.png,.heic,.heif",
  onChange,
  multiple = true,
  primaryMessage = "Drag and drop your document",
  secondaryMessage = "We recommend starting with a will, trust, or death certificate",
  disabled = false,
  disabledMessage = "Maximum number of documents reached",
  className,
}: DocumentUploadInputProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange && !disabled) {
        onChange(event);
        // Reset the input value so the same file can be selected again
        // (e.g., after deleting a document and re-adding the same file)
        event.target.value = "";
      }
    },
    [onChange, disabled],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      // Check if the dataTransfer object and its files property exist
      if (event.dataTransfer && event.dataTransfer.files) {
        const fileList = event.dataTransfer.files;

        if (fileList.length > 0) {
          const syntheticEvent = {
            target: { files: fileList },
          } as React.ChangeEvent<HTMLInputElement>;

          handleChange(syntheticEvent);
        }
      }
    },
    [handleChange, disabled],
  );

  const content = (
    <label
      htmlFor={disabled ? undefined : "upload-input"}
      className={cn([
        "group border-2 border-dashed border-stone-200 rounded-lg p-8 md:p-20 flex flex-col items-center justify-center gap-2 relative",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        isDragging
          ? "border-4 border-teal-500 bg-teal-50"
          : "border-stone-200 hover:border-teal-600",
        className,
      ])}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="p-2 md:p-4 flex items-center justify-center">
        <Upload size={24} className="md:w-8 md:h-8 text-stone-300" />
      </div>
      <p className="text-base md:text-lg text-stone-900 font-medium text-center">
        <span className="block md:hidden">Click here to upload</span>
        <span className="hidden md:inline">
          {primaryMessage}
          {" or "}
          <span className="text-teal-600 underline">choose files from your computer</span>
        </span>
      </p>
      <p className="text-sm text-stone-600 text-center">{secondaryMessage}</p>
      {!disabled && (
        <input
          id="upload-input"
          type="file"
          accept={accept}
          multiple={multiple}
          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleChange}
        />
      )}
    </label>
  );

  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p className="text-white">{disabledMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
